import React, {ChangeEvent, FC, ReactNode, SyntheticEvent, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {useLocation} from 'wouter';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {ClickAwayListener} from '@mui/material';
import './Searchbar.less';
import IconButton from '../ui/IconButton/IconButton';
import Icons from '../ui/IconButton/Icons';
import Checkbox, {CheckboxClickEvent} from '../ui/Checkbox';
import useComponentCache from '../hooks/useComponentCache';
import {REPORT_CATEGORIES} from '../Reports/Reports';
import {CONFIG_CATEGORIES} from '../Configuration/Config';
import {SUPPORT_PAGES} from '../Support/Support';
import {PROFILE_PAGES, BILLING_PAGES} from './dialogPages';
import {dialogNoButtons} from '../ui/Confirm';
import MyProfileDialog from '../ui/UserMenu/MyProfileDialog';
import BillingDialog from '../ui/UserMenu/BillingDialog';
import Auth from '../Auth/Auth';
import {getCurrentUserId} from '@/lib/picos/currentLogin';
import {MOBILE_WIDTH} from '@/lib/picos/breakpoints';
import CaseHelper from '../Case/CaseHelper';
import {formatLFM, formatFML, isUnique} from '../Contacts/ContactFormat';
import type Matter from '../entities/matter';
import type {RootState} from '@/@types/store';
import type {IconType} from '../ui/IconButton/Icons';

export type SearchOptions = Partial<{
    caseSensitive: boolean,
    fuzzy: boolean,
    matchAll: boolean,
    wholeWords: boolean,
    deep: boolean
}>;

export type SearchHandler = (terms: string[], options: SearchOptions) => void;

export type SearchProps = Partial<{
    keyName: string,
    onSearch: SearchHandler;
    showOptions: boolean,
    showEntityResults: boolean,
    terms: string[]
    options: SearchOptions,
}>;

type SearchResult = {
    type: string,
    label: string,
    secondary?: string,
    route: string,
    icon?: IconType,
    iconColor?: string,
    renderIcon?: () => ReactNode,
    action?: () => void,
};

type SearchMode = 'filter' | 'global';

type SearchbarState = {
    hasText: boolean,
    searchTerms: string,
    options: SearchOptions,
    expand: boolean,
    searchMode: SearchMode,
}

const MAX_RESULTS_PER_TYPE = 5;

// Disposition sort order matching CaseCard dropdown
const DISPOSITION_ORDER: Record<string, number> = {o: 0, c: 1, w: 2, p: 3, h: 4};

/**
 * Format a contact name as "Last, First" with a middle initial added only when
 * another contact shares the same last + first name.
 */
import type Contact from '../entities/contact';
const formatContactName = (contact: Contact): string => {
    if (!contact) return '';
    const base = formatLFM(contact);
    if (contact.middle && !isUnique({last: contact.last, first: contact.nick ?? contact.first}, {})) {
        return `${base} ${contact.middle[0]}.`;
    }
    return base;
};

/** FML format for use in comma-separated lists (e.g., inside parentheses) */
const formatContactNameFML = (contact: Contact): string => {
    if (!contact) return '';
    const base = formatFML(contact);
    if (contact.middle && !isUnique({last: contact.last, first: contact.nick ?? contact.first}, {})) {
        const first = contact.nick ?? contact.first ?? '';
        return `${first} ${contact.middle[0]}. ${contact.last ?? ''}`.trim();
    }
    return base;
};

/**
 * Search input with global search across entities, reports, and config,
 * plus page-level filtering via the onSearch callback.
 */
const Searchbar: FC<SearchProps> = (props) => {
    const neverShow = props.showOptions === false;

    const {cacheSave, cacheValue} = useComponentCache<SearchbarState>(`search-${props.keyName?.toLowerCase().replaceAll(' ', '-')}`);

    const [hasText, setHasText] = useState<boolean>(cacheValue('hasText', false));
    const [searchTerms, setSearchTerms] = useState<string>(cacheValue('searchTerms', ''));
    const [options, setOptions] = useState<SearchOptions>(cacheValue('options', {
        caseSensitive: false,
        fuzzy: false,
        matchAll: true,
        wholeWords: false,
        deep: false
    }));
    const [expand, setExpand] = useState<boolean>(cacheValue('expand', false));
    const [searchMode, setSearchMode] = useState<SearchMode>(cacheValue('searchMode', 'filter'));
    const [showResults, setShowResults] = useState<boolean>(false);
    const [showConfig, setShowConfig] = useState<boolean>(false);
    const [showModeMenu, setShowModeMenu] = useState<boolean>(false);
    const [selectedIndex, setSelectedIndex] = useState<number>(-1);

    // Use deferred value for automatic debouncing
    const deferredSearchTerms = useDeferredValue(searchTerms);

    const {onSearch} = props;
    const [, navigate] = useLocation();

    // Subscribe to roles so admin check re-evaluates when auth data loads
    useSelector((state: RootState) => state.roles);

    // Redux entity stores
    const contacts = useSelector((state: RootState) => state.contacts);
    const leads = useSelector((state: RootState) => state.leads);
    const matters = useSelector((state: RootState) => state.matters);
    const campaigns = useSelector((state: RootState) => state.campaigns);
    const events = useSelector((state: RootState) => state.events);
    const lookups = useSelector((state: RootState) => state.lookups);
    const isPortal = useSelector((state: RootState) => state.misc.portalUser);

    const isGlobal = props.showEntityResults && searchMode === 'global';

    // Execute page-level filter when deferred value changes (filter mode only)
    useEffect(() => {
        if (onSearch) {
            if (isGlobal) {
                onSearch([], options);
            } else {
                const terms = deferredSearchTerms ? deferredSearchTerms.split(' ') : [];
                onSearch(terms, options);
            }
        }
    }, [deferredSearchTerms, onSearch, options, isGlobal]);

    // Save cache whenever state changes
    useEffect(() => {
        return () => {
            cacheSave({hasText, searchTerms, options, expand, searchMode});
        }
    }, [cacheSave, expand, hasText, options, searchTerms, searchMode]);

    // Compute global search results (only in global search mode)
    const searchResults = useMemo<SearchResult[]>(() => {
        if (!isGlobal) return [];
        if (!deferredSearchTerms || deferredSearchTerms.trim().length === 0) return [];

        const terms = deferredSearchTerms.split(' ').filter(t => t.length > 0);
        if (terms.length === 0) return [];

        const results: SearchResult[] = [];
        const lowerTerms = terms.map(t => t.toLowerCase());

        // Search contacts
        const matchedContacts = contacts.filter(c => c.status !== 'D' && c.search(terms, options));
        matchedContacts.forEach((c, i) => {
            if (i >= MAX_RESULTS_PER_TYPE) return;
            results.push({
                type: 'Contacts',
                label: formatContactName(c),
                route: `/contacts/${c.id}`,
                icon: Icons.CONTACT,
                iconColor: 'blue',
            });
        });

        // Search matters — match on matter fields OR client contact names, sort by disposition
        const getClientNames = (m: Matter): string => {
            if (!m.clients?.length) return '';
            return m.clients
                .map(id => contacts.get(id))
                .filter(c => c && c.status !== 'D')
                .map(c => formatContactNameFML(c!))
                .join(', ');
        };

        const matterMatchesClients = (m: Matter): boolean => {
            if (!m.clients?.length) return false;
            return m.clients.some(id => {
                const c = contacts.get(id);
                return c && c.status !== 'D' && c.search(terms, options);
            });
        };

        const matchedMatters = matters
            .filter(m => m.status !== 'D' && (m.search(terms, options) || matterMatchesClients(m)))
            .values();
        const sortedMatters = [...matchedMatters]
            .sort((a, b) => (DISPOSITION_ORDER[a.disposition] ?? 99) - (DISPOSITION_ORDER[b.disposition] ?? 99));
        for (let i = 0; i < Math.min(sortedMatters.length, MAX_RESULTS_PER_TYPE); i++) {
            const m = sortedMatters[i];
            const clientNames = getClientNames(m);
            results.push({
                type: 'Cases',
                label: m.name,
                secondary: clientNames ? `(${clientNames})` : undefined,
                route: `/cases/${m.id}`,
                renderIcon: () => CaseHelper.renderDispositionIcon(m),
            });
        }

        // Search leads
        const getLeadClientNames = (l: { clients?: string[] | null }): string => {
            if (!l.clients?.length) return '';
            return l.clients
                .map(id => contacts.get(id))
                .filter(c => c && c.status !== 'D')
                .map(c => formatContactNameFML(c!))
                .join(', ');
        };

        const matchedLeads = leads.filter(l => l.status !== 'D' && l.search(terms, options));
        matchedLeads.forEach((l, i) => {
            if (i >= MAX_RESULTS_PER_TYPE) return;
            const clientNames = getLeadClientNames(l);
            results.push({
                type: 'Leads',
                label: l.name,
                secondary: clientNames ? `(${clientNames})` : undefined,
                route: `/sales/${l.id}`,
                icon: Icons.LEAD,
                iconColor: 'green',
            });
        });


        // Search campaigns
        const matchedCampaigns = campaigns.filter(c => c.status !== 'D' && c.search(terms, options));
        matchedCampaigns.forEach((c, i) => {
            if (i >= MAX_RESULTS_PER_TYPE) return;
            results.push({
                type: 'Campaigns',
                label: c.name,
                route: `/marketing/campaigns?item=${c.id}`,
                icon: Icons.MARKETING,
            });
        });

        // Search events — resolve entityId to build label and route
        const matchedEvents = events.filter(ev => ev.status !== 'D' && ev.search(terms, options));
        matchedEvents.forEach((ev, i) => {
            if (i >= MAX_RESULTS_PER_TYPE) return;
            if (!ev.entityId) return;

            const categoryLookup = ev.category ? lookups.find(l => l.path === 'contact:event:category' && l.id === ev.category) : null;
            const categoryName = categoryLookup?.key;
            const desc = ev.description || '';
            const evParts = [categoryName, desc].filter(Boolean).join(' - ');
            const evName = evParts || '(no description)';

            // Check if entityId is directly a matter
            const directMatter = matters.get(ev.entityId);
            if (directMatter) {
                const clientNames = getClientNames(directMatter);
                results.push({
                    type: 'Events',
                    label: evName,
                    secondary: clientNames ? `from ${directMatter.name} (${clientNames})` : `from ${directMatter.name}`,
                    route: `/cases/${directMatter.matterId}?event=${ev.id}`,
                    icon: Icons.EVENT,
                    iconColor: 'purple',
                });
                return;
            }

            // Check if entityId is a contact — just show the event name with contact context
            const contact = contacts.get(ev.entityId);
            if (contact && contact.status !== 'D') {
                const related = matters.find(m =>
                    m.status !== 'D' && (m.clients || []).includes(ev.entityId)
                );
                if (related) {
                    results.push({
                        type: 'Events',
                        label: evName,
                        secondary: `from ${formatContactNameFML(contact)}`,
                        route: `/cases/${related.matterId}?event=${ev.id}`,
                        icon: Icons.EVENT,
                        iconColor: 'purple',
                    });
                }
                return;
            }

            // Check if entityId is a lead
            const lead = leads.get(ev.entityId);
            if (lead) {
                const related = matters.find(m =>
                    m.status !== 'D' && m.leadId === ev.entityId
                );
                if (related) {
                    const clientNames = getLeadClientNames(lead);
                    const leadLabel = clientNames ? `${lead.name} (${clientNames})` : lead.name;
                    results.push({
                        type: 'Events',
                        label: evName,
                        secondary: `from ${leadLabel}`,
                        route: `/cases/${related.matterId}?event=${ev.id}`,
                        icon: Icons.EVENT,
                        iconColor: 'purple',
                    });
                }
            }
        });

        // Search reports and config — match at word boundaries only
        const matchesWordStart = (label: string, term: string) => {
            const idx = label.indexOf(term);
            return idx === 0 || (idx > 0 && /\W/.test(label[idx - 1]));
        };
        const labelMatches = (label: string) => {
            const l = label.toLowerCase();
            return options.matchAll !== false
                ? lowerTerms.every(t => matchesWordStart(l, t))
                : lowerTerms.some(t => matchesWordStart(l, t));
        };

        // Search reports
        for (const category of REPORT_CATEGORIES) {
            for (const item of category.items) {
                const matches = labelMatches(item.label);
                if (matches) {
                    results.push({
                        type: 'Reports',
                        label: item.label,
                        route: `/reports/${item.page}`,
                        icon: item.icon,
                    });
                }
            }
        }

        // Search config (admin only, static label match)
        if (Auth.isAdmin(getCurrentUserId())) {
            for (const category of CONFIG_CATEGORIES) {
                for (const item of category.items) {
                    const matches = labelMatches(item.label);
                    if (matches) {
                        results.push({
                            type: 'Configuration',
                            label: `${category.name}: ${item.label}`,
                            route: `/config/${item.page}`,
                            icon: item.icon,
                        });
                    }
                }
            }
        }

        // Search Support pages — portal users see only portalVisible tabs.
        // Match the tab label itself plus any sub-items (action buttons, toggles).
        // Sub-item results route to the parent tab; user finds the control there.
        for (const page of SUPPORT_PAGES) {
            if (isPortal && !page.portalVisible) continue;
            if (labelMatches(page.label)) {
                results.push({
                    type: 'Support',
                    label: page.label,
                    route: `/support/${page.urlKey}`,
                    icon: page.icon,
                });
            }
            for (const item of page.items ?? []) {
                if (labelMatches(item)) {
                    results.push({
                        type: 'Support',
                        label: `${page.label}: ${item}`,
                        route: `/support/${page.urlKey}`,
                        icon: page.icon,
                    });
                }
            }
        }

        // Search My Profile — opens dialog instead of navigating
        for (const p of PROFILE_PAGES) {
            if (labelMatches(p.label)) {
                results.push({
                    type: 'My Profile',
                    label: p.label,
                    route: '',
                    icon: p.icon,
                    iconColor: p.iconColor,
                    action: () => dialogNoButtons('My Profile', <MyProfileDialog/>, {maxWidth: 500}),
                });
            }
        }

        // Search Billing — owner only, not portal; opens dialog
        if (Auth.isOwner() && !isPortal) {
            for (const p of BILLING_PAGES) {
                if (labelMatches(p.label)) {
                    results.push({
                        type: 'Billing',
                        label: p.label,
                        route: '',
                        icon: p.icon,
                        iconColor: p.iconColor,
                        action: () => dialogNoButtons('Billing', <BillingDialog/>, {maxWidth: 600}),
                    });
                }
            }
        }

        return results;
    }, [isGlobal, deferredSearchTerms, options, contacts, matters, leads, campaigns, events, lookups, isPortal]);

    // Reset selected index when results change
    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional reset when search results identity changes
        setSelectedIndex(-1);
    }, [searchResults]);

    const input = useRef<HTMLInputElement>(null);
    const resultsRef = useRef<HTMLDivElement>(null);

    const onCancelSearch = useCallback((e?: SyntheticEvent | null) => {
        if (e) e.preventDefault();

        if (input.current) {
            input.current.blur();
        }

        setSearchTerms('');
        setHasText(false);
        setShowResults(false);
        setShowConfig(false);
        setExpand(false);
        setSelectedIndex(-1);
    }, []);

    const navigateToResult = useCallback((result: SearchResult) => {
        if (result.action) result.action();
        else navigate(result.route);
        setShowResults(false);
        setSelectedIndex(-1);
    }, [navigate]);

    const onKeyPress = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') {
            onCancelSearch();
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex(prev => Math.min(prev + 1, searchResults.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex(prev => Math.max(prev - 1, -1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (selectedIndex >= 0 && selectedIndex < searchResults.length) {
                navigateToResult(searchResults[selectedIndex]);
            } else {
                setShowResults(false);
                if (input.current) input.current.blur();
            }
        } else if (e.key === 'Tab') {
            e.preventDefault();
            setShowResults(false);
            setShowConfig(false);
            if (input.current) input.current.blur();
        }
    }, [onCancelSearch, searchResults, selectedIndex, navigateToResult]);

    const onBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
        e.preventDefault();
    }, []);

    const handleSearch = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const _searchValue = e.target.value;
        const _hasText = _searchValue.length > 0;

        setSearchTerms(_searchValue);
        setHasText(_hasText);
        setShowResults(_hasText && isGlobal);
        setShowConfig(false);
    }, [isGlobal]);

    const onFuzzySearch: CheckboxClickEvent = useCallback(e => {
        e.preventDefault();
        e.persist();
        setOptions(prev => ({...prev, fuzzy: !prev.fuzzy}));
    }, []);

    const onMatchAll: CheckboxClickEvent = useCallback(e => {
        e.preventDefault();
        e.persist();
        setOptions(prev => ({...prev, matchAll: !prev.matchAll}));
    }, []);

    const onCaseSensitive: CheckboxClickEvent = useCallback(e => {
        e.preventDefault();
        e.persist();
        setOptions(prev => ({...prev, caseSensitive: !prev.caseSensitive}));
    }, []);

    const onWholeWords: CheckboxClickEvent = useCallback(e => {
        e.preventDefault();
        e.persist();
        setOptions(prev => ({...prev, wholeWords: !prev.wholeWords}));
    }, []);

    const onSearchClick = useCallback(() => {
        setSearchTerms('');
        setExpand(e => !e);
        input.current?.focus();
    }, []);

    const onModeIconClick = useCallback(() => {
        if (!expand && window.innerWidth <= MOBILE_WIDTH) {
            setExpand(true);
            input.current?.focus();
        } else {
            setShowModeMenu(prev => !prev);
            setShowResults(false);
            setShowConfig(false);
        }
    }, [expand]);

    const onSelectMode = useCallback((mode: SearchMode) => {
        setSearchMode(prev => {
            if (prev !== mode) {
                setShowResults(mode === 'global' && hasText);
            }
            return mode;
        });
        setShowModeMenu(false);
        input.current?.focus();
    }, [hasText]);

    const onConfigClick = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setShowConfig(prev => !prev);
        setShowResults(false);
        setShowModeMenu(false);
    }, []);

    const onInputClick = useCallback((e: React.MouseEvent<HTMLInputElement>) => {
        e.preventDefault();
        if (isGlobal && hasText && searchResults.length > 0) {
            setShowResults(true);
        }
        setShowConfig(false);
    }, [isGlobal, hasText, searchResults.length]);

    const {fuzzy, matchAll, caseSensitive, wholeWords} = options;

    const expandClass = expand ? 'search-bar--expand' : '';
    const inUse = hasText ? 'search-bar--in-use' : '';
    const dropdownOpen = (showResults && searchResults.length > 0) || showConfig || showModeMenu ? 'search-bar--dropdown-open' : '';

    // Group results by type for rendering
    const groupedResults = useMemo(() => {
        const groups: {type: string, items: {result: SearchResult, globalIndex: number}[]}[] = [];
        let currentType = '';
        searchResults.forEach((result, index) => {
            if (result.type !== currentType) {
                currentType = result.type;
                groups.push({type: currentType, items: []});
            }
            groups[groups.length - 1].items.push({result, globalIndex: index});
        });
        return groups;
    }, [searchResults]);

    // Scroll selected item into view
    useEffect(() => {
        if (selectedIndex >= 0 && resultsRef.current) {
            const items = resultsRef.current.querySelectorAll('.search-bar__result-item');
            items[selectedIndex]?.scrollIntoView({block: 'nearest'});
        }
    }, [selectedIndex]);

    return (
        <ClickAwayListener onClickAway={() => { setShowResults(false); setShowConfig(false); setShowModeMenu(false); }}>
            <div className={`search-bar ${expandClass} ${inUse} ${dropdownOpen}`.trim()}>
                <div className="search-bar__main">
                    <div className={`search-bar__icons ${props.showEntityResults ? 'search-bar__icons--mode' : ''}`}
                         onClick={props.showEntityResults ? onModeIconClick : onSearchClick}>
                        <IconButton type={isGlobal ? Icons.GLOBE : Icons.SEARCH}/>
                        {props.showEntityResults &&
                            <FontAwesomeIcon icon={Icons.CHEVRON_DOWN.reactIcon} className="search-bar__mode-caret"/>
                        }
                    </div>
                    <div className="search-bar__input-area">
                        <input ref={input}
                               autoComplete="nope"
                               type="text"
                               placeholder={isGlobal ? 'Search everything...' : props.showEntityResults ? 'Search this page...' : 'Search...'}
                               onClick={onInputClick}
                               onChange={handleSearch}
                               onBlur={onBlur}
                               onKeyDown={onKeyPress}
                               value={hasText ? searchTerms : ''}
                               data-lpignore={true}
                               tabIndex={1}
                        />
                        {hasText &&
                            <div className="search-bar__clear-icon">
                                <IconButton type={Icons.SEARCH_CANCEL} onClick={onCancelSearch} tabIndex={0}/>
                            </div>
                        }
                    </div>
                    {!neverShow &&
                        <div className="search-bar__config-icon">
                            <IconButton type={Icons.CONFIG} onClick={onConfigClick} title="Search options"/>
                        </div>
                    }
                </div>

                {/* Search results dropdown */}
                {showResults && searchResults.length > 0 &&
                    <div className="search-bar__results" ref={resultsRef}>
                        {groupedResults.map(group => (
                            <div key={group.type} className="search-bar__result-group">
                                <div className="search-bar__result-group-header">{group.type}</div>
                                {group.items.map(({result, globalIndex}) => (
                                    <div key={`${result.type}-${result.route || result.label}`}
                                         className={`search-bar__result-item ${globalIndex === selectedIndex ? 'search-bar__result-item--selected' : ''}`}
                                         onMouseDown={(e) => { e.preventDefault(); navigateToResult(result); }}
                                         onMouseEnter={() => setSelectedIndex(globalIndex)}
                                    >
                                        <span className="search-bar__result-icon">
                                            {result.renderIcon
                                                ? result.renderIcon()
                                                : result.icon && <FontAwesomeIcon icon={result.icon.reactIcon} color={result.iconColor}/>
                                            }
                                        </span>
                                        <span className="search-bar__result-label">
                                            <span className="search-bar__result-primary">{result.label}</span>
                                            {result.secondary && <span className="search-bar__result-secondary"> {result.secondary}</span>}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                }

                {/* Config dropdown for search options */}
                {showConfig &&
                    <div className="search-bar__config-menu">
                        <Checkbox onClick={onFuzzySearch}
                                  checked={fuzzy}
                                  label='Use Fuzzy Search'
                        />
                        <Checkbox onClick={onMatchAll}
                                  checked={matchAll}
                                  label='Match All Terms'
                        />
                        <Checkbox onClick={onCaseSensitive}
                                  checked={caseSensitive}
                                  label='Case Sensitive'
                                  disabled={fuzzy}
                        />
                        <Checkbox onClick={onWholeWords}
                                  checked={wholeWords}
                                  label='Match Whole Words'
                                  disabled={fuzzy}
                        />
                    </div>
                }

                {/* Search mode dropdown */}
                {showModeMenu &&
                    <div className="search-bar__mode-menu">
                        <div className={`search-bar__mode-item ${searchMode === 'filter' ? 'search-bar__mode-item--active' : ''}`}
                             onMouseDown={(e) => { e.preventDefault(); onSelectMode('filter'); }}>
                            <FontAwesomeIcon icon={Icons.SEARCH.reactIcon}/>
                            <span>Search this page</span>
                        </div>
                        <div className={`search-bar__mode-item ${searchMode === 'global' ? 'search-bar__mode-item--active' : ''}`}
                             onMouseDown={(e) => { e.preventDefault(); onSelectMode('global'); }}>
                            <FontAwesomeIcon icon={Icons.GLOBE.reactIcon}/>
                            <span>Search everything</span>
                        </div>
                    </div>
                }
            </div>
        </ClickAwayListener>
    );
}

export default Searchbar;
